import { Link } from "react-router-dom";
import { IconCalendarOff } from "@tabler/icons-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

// Only for a response that arrives without one. The server always sends the season's website
// message while booking is closed, and admins edit that text — this is never meant to be read.
const FALLBACK_MESSAGE = "We are not booking new appointments at this time.";

const TITLE = "Appointment Booking Is Closed";

/**
 * What a member of the public sees instead of anything that starts a booking while public booking is
 * closed: before the booking season, after it, or with none set. One message for every reason — the
 * season's website message, as the server sent it — and never the reason or the dates.
 *
 * The message is admin-entered plain text. It is rendered as text, never as HTML, with the admin's
 * line breaks kept.
 *
 * `page` is the full-width terminal on /booking/new, laid out like the flow's other terminals and
 * pointing at Manage Existing Appointment, which keeps working while booking is closed. `inline` sits
 * in the /booking action column in place of the New Appointment button, which already has the manage
 * button beneath it.
 */
export function BookingSeasonClosedNotice({
  message,
  variant = "page",
}: {
  message: string | null | undefined;
  variant?: "page" | "inline";
}) {
  const text = message || FALLBACK_MESSAGE;

  if (variant === "inline") {
    return (
      <Alert className="border-amber-200 bg-amber-50/50">
        <IconCalendarOff className="text-amber-600" />
        <AlertTitle className="text-slate-900">{TITLE}</AlertTitle>
        <AlertDescription className="text-slate-700 whitespace-pre-line">{text}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="w-full max-w-xl mx-auto text-center space-y-5 pt-10 pb-6 px-4">
      <div className="mx-auto w-16 h-16 bg-amber-100 flex items-center justify-center rounded-full">
        <IconCalendarOff className="w-8 h-8 text-amber-600" />
      </div>
      <h2 className="text-2xl font-semibold text-slate-900">{TITLE}</h2>
      <p className="text-slate-600 whitespace-pre-line">{text}</p>
      <Button asChild variant="outline">
        <Link to="/booking/manage">Manage Existing Appointment</Link>
      </Button>
    </div>
  );
}
